import {Injectable} from '@angular/core'
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router'
import {Observable, of, switchMap} from 'rxjs'
import {catchError, map} from 'rxjs/operators'
import {ResolverUtilService} from '../../../core/resolver/resolver-util.service'
import {
  GetPublicProProfileGql,
  PublicMinimalProProfileFieldsFragment,
  PublicProWithProfileFieldsFragment,
  PublicPublishedProProfileFieldsFragment,
} from '../../../../__generated__/types'
import {pickProperty} from '../../../core/graphql/graphql-utils'

export type ProWithProfile = Omit<PublicProWithProfileFieldsFragment, 'profile'> & {
  minimalProfile?: PublicMinimalProProfileFieldsFragment
  publishedProfile?: PublicPublishedProProfileFieldsFragment
}

@Injectable({
  providedIn: 'root',
})
export class PublicProProfileResolver {
  constructor(
    private readonly util: ResolverUtilService,
    private readonly getProProfileGql: GetPublicProProfileGql,
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<ProWithProfile> {
    const slug = route.paramMap.get('slug')
    if (!slug) {
      return this.util.redirectToNotFound(state.url)
    }

    return this.getProProfileGql.fetch({slug}, {fetchPolicy: 'no-cache'}).pipe(
      map(res => pickProperty(res.data, 'proBySlug')),
      map(res => {
        if (!res) {
          throw new Error(`Profile for "${slug}" not found`)
        } else {
          return res
        }
      }),
      switchMap(res => {
        if (!res.profile) {
          return this.util.redirectToNotFound(state.url)
        } else {
          const {profile, ...pro} = res
          return of({
            ...pro,
            minimalProfile: profile.__typename === 'PublicMinimalProProfile' ? profile : undefined,
            publishedProfile: profile.__typename === 'PublicPublishedProProfile' ? profile : undefined,
          })
        }
      }),
      catchError(e => this.util.handleError(e, state.url)),
    )
  }
}
